import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Dropdown,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  EyeOutlined,
  LockOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  ShopOutlined,
  UserAddOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import CopyableText from "common/CopyableText";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import {
  deleteWorkspace,
  fetchWorkspaceInvitePrivileges,
  fetchWorkspaceInviteRoles,
  fetchWorkspace,
  fetchWorkspaceMembers,
  inviteWorkspaceUser,
  removeWorkspaceMember,
  updateWorkspace,
  withdrawWorkspaceInvitation,
} from "services/workspaceService";
import olreadiaService from "services/olreadiaService";
import identitySubscriptionService from "services/subscriptions";
import paths from "routes/paths";
import WorkspaceMemberCard, {
  WorkspaceMembers,
} from "./WorkspaceMembers";
import {
  organizationName,
  formatDateTime,
  hasAdminAccess,
  membershipUserEmail,
  membershipUserId,
  membershipUserLabel,
  readable,
  subscriptionLabel,
} from "./workspaceUtils";
import "./WorkspaceDetailsPage.css";
import { OLIVEEB_COLORS, workspaceButtonStyles, workspaceStyles } from "./colors";

const { Text, Title } = Typography;

const toRoleLabel = (role) =>
  [role?.name, role?.type, role?.owner].filter(Boolean).join(" • ") ||
  role?.id ||
  "Role";

const toPrivilegeLabel = (privilege) =>
  [privilege?.name || privilege?.scope, privilege?.description]
    .filter(Boolean)
    .join(" • ") ||
  privilege?.id ||
  "Privilege";

const sectionTitle = (title) => (
  <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
    {title}
  </Text>
);

const isPersonalAccount = (account) =>
  String(account?.accountType || "").toUpperCase() === "PERSONAL";

const membershipStatus = (membership) =>
  String(membership?.membershipStatus || membership?.status || "").toUpperCase();

const isActiveMembership = (membership) => {
  const status = membershipStatus(membership);
  return !status || status === "ACTIVE" || status === "ACCEPTED";
};

const isPendingMembership = (membership) => membershipStatus(membership) === "PENDING";

const workspaceInvitationLink = (workspaceId, invitationId) => {
  if (!workspaceId || !invitationId || typeof window === "undefined") return "";
  return `${window.location.origin}${paths.workspaceInvitation(workspaceId, invitationId)}`;
};

const WorkspaceDetailsPage = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshWorkspaces } = useWorkspace();
  const actorId = user?.id || user?.userId || user?.uid || "";
  const [account, setAccount] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [updatingWorkspace, setUpdatingWorkspace] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupLoaded, setLookupLoaded] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [privilegeOptions, setPrivilegeOptions] = useState([]);
  const [roleSearch, setRoleSearch] = useState("");
  const [privilegeSearch, setPrivilegeSearch] = useState("");
  const [organization, setOrganization] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [editForm] = Form.useForm();
  const [inviteForm] = Form.useForm();
  const selectedRoleIds = Form.useWatch("roleIds", inviteForm) || [];
  const selectedPrivilegeIds = Form.useWatch("privilegeIds", inviteForm) || [];

  const myMembership = useMemo(() => {
    const list = account?.users || [];
    return list.find((member) => membershipUserId(member) === actorId) || null;
  }, [account?.users, actorId]);

  const isProductAdmin = useMemo(() => hasAdminAccess(myMembership), [myMembership]);

  const loadDetails = useCallback(async () => {
    if (!workspaceId || !actorId) return;

    try {
      setLoading(true);
      const nextAccount = await fetchWorkspace(workspaceId, actorId);
      setAccount(nextAccount);

      if (!isPersonalAccount(nextAccount) && nextAccount.organizationId) {
        try {
          const entity = await olreadiaService.organizations.get(nextAccount.organizationId);
          setOrganization(entity || {});
        } catch (error) {
          setOrganization(null);
        }
      } else {
        setOrganization(null);
      }

      try {
        const nextSubscription = await identitySubscriptionService.subscriptions.activeForWorkspace(
          nextAccount,
          actorId,
        );
        setSubscription(nextSubscription || null);
      } catch (error) {
        setSubscription(null);
      }

      const ownMembership =
        (nextAccount.users || []).find((member) => membershipUserId(member) === actorId) ||
        null;

      if (hasAdminAccess(ownMembership)) {
        const page = await fetchWorkspaceMembers({
          actorId,
          workspaceId: workspaceId,
          size: 100,
        });
        setMembers(page.content);
      } else {
        setMembers([]);
      }
    } catch (error) {
      message.error(error?.message || "Failed to load workspace.");
    } finally {
      setLoading(false);
    }
  }, [actorId, workspaceId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const visibleMemberships = members.length > 0 ? members : account?.users || [];
  const activeMembers = visibleMemberships.filter(isActiveMembership);
  const pendingInvitations = visibleMemberships.filter(isPendingMembership);
  const canInvite =
    isProductAdmin && String(account?.accountType || "").toUpperCase() === "ORGANIZATION";

  const closeInvite = () => {
    setInviteOpen(false);
    setRoleSearch("");
    setPrivilegeSearch("");
    inviteForm.resetFields();
  };

  const loadInviteLookups = useCallback(async () => {
    if (lookupLoading || lookupLoaded) return;

    try {
      setLookupLoading(true);
      const [roles, privileges] = await Promise.all([
        fetchWorkspaceInviteRoles(),
        fetchWorkspaceInvitePrivileges(),
      ]);

      setRoleOptions(
        roles.map((role) => ({
          value: role.id,
          label: toRoleLabel(role),
          searchText: [role.name, role.type, role.owner, role.description, role.id]
            .filter(Boolean)
            .join(" ")
            .toLowerCase(),
        })),
      );
      setPrivilegeOptions(
        privileges.map((privilege) => ({
          value: privilege.id,
          label: toPrivilegeLabel(privilege),
          searchText: [
            privilege.name,
            privilege.scope,
            privilege.description,
            privilege.id,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase(),
        })),
      );
    } catch (error) {
      message.error(error?.message || "Failed to load roles and privileges.");
    } finally {
      setLookupLoaded(true);
      setLookupLoading(false);
    }
  }, [lookupLoaded, lookupLoading]);

  useEffect(() => {
    if (canInvite && !lookupLoaded) {
      loadInviteLookups();
    }
  }, [canInvite, loadInviteLookups, lookupLoaded]);

  const openInvite = () => {
    setInviteOpen(true);
  };

  const openEditWorkspace = () => {
    editForm.setFieldsValue({
      name: account?.name || "",
      description: account?.description || "",
    });
    setEditOpen(true);
  };

  const closeEditWorkspace = () => {
    setEditOpen(false);
    editForm.resetFields();
  };

  const submitWorkspaceUpdate = async (values) => {
    const name = String(values.name || "").trim();
    const description = String(values.description || "").trim();

    if (!name) {
      message.error("Enter a workspace name.");
      return;
    }

    try {
      setUpdatingWorkspace(true);
      const updatedAccount = await updateWorkspace({
        actorId,
        workspaceId: workspaceId,
        name,
        description,
      });
      setAccount(updatedAccount || { ...account, name, description });
      await refreshWorkspaces();
      message.success("Workspace updated.");
      closeEditWorkspace();
    } catch (error) {
      message.error(error?.message || "Failed to update workspace.");
    } finally {
      setUpdatingWorkspace(false);
    }
  };

  const visibleRoleOptions = roleSearch.trim()
    ? roleOptions
    : roleOptions.filter((option) => selectedRoleIds.includes(option.value));
  const visiblePrivilegeOptions = privilegeSearch.trim()
    ? privilegeOptions
    : privilegeOptions.filter((option) => selectedPrivilegeIds.includes(option.value));

  const submitInvite = async (values) => {
    const email = String(values.email || "").trim();
    const userId = String(values.userId || "").trim();

    if (!email && !userId) {
      message.error("Enter an email address or user ID.");
      return;
    }

    try {
      setInviting(true);
      await inviteWorkspaceUser({
        actorId,
        workspaceId: workspaceId,
        email,
        userId,
        roleIds: values.roleIds || [],
        privilegeIds: values.privilegeIds || [],
      });
      message.success("Invitation sent.");
      closeInvite();
      await loadDetails();
    } catch (error) {
      message.error(error?.message || "Failed to invite user.");
    } finally {
      setInviting(false);
    }
  };

  const withdrawInvitation = async (membership) => {
    if (!membership?.id) {
      message.error("Invitation id is missing.");
      return;
    }

    try {
      await withdrawWorkspaceInvitation({
        actorId,
        workspaceId: workspaceId,
        invitationId: membership.id,
      });
      message.success("Invitation withdrawn.");
      await loadDetails();
    } catch (error) {
      message.error(error?.message || "Failed to withdraw invitation.");
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!workspaceId) return;

    try {
      setDeleting(true);
      await deleteWorkspace({ actorId, workspaceId: workspaceId });
      message.success("Workspace deleted.");
      await refreshWorkspaces();
      navigate(paths.workspaces);
    } catch (error) {
      message.error(error?.message || "Failed to delete workspace.");
    } finally {
      setDeleting(false);
    }
  };

  const viewMember = (membership) => {
    const userId = membershipUserId(membership);
    if (!userId) {
      message.error("Member user id is missing.");
      return;
    }
    navigate(paths.workspaceMemberDetails(workspaceId, userId));
  };

  const canRemoveMember = (membership) => {
    const memberId = membershipUserId(membership);
    return Boolean(
      isProductAdmin &&
      membership?.id &&
      memberId &&
      memberId !== actorId &&
      isActiveMembership(membership),
    );
  };

  const removeMember = async (membership) => {
    const memberId = membershipUserId(membership);
    if (!membership?.id) {
      message.error("Membership id is missing.");
      return;
    }
    if (!memberId) {
      message.error("Member user id is missing.");
      return;
    }

    try {
      setRemovingMemberId(membership.id);
      const status = String(membership?.membershipStatus || "").toUpperCase();
      if (status === "PENDING") {
        await withdrawWorkspaceInvitation({
          actorId,
          workspaceId: workspaceId,
          invitationId: membership.id,
        });
      } else {
        await removeWorkspaceMember({
          actorId,
          workspaceId: workspaceId,
          membershipId: memberId,
        });
      }
      message.success("Member removed.");
      await loadDetails();
    } catch (error) {
      message.error(error?.message || "Failed to remove member.");
    } finally {
      setRemovingMemberId("");
    }
  };

  const linkedOrganizationName = account?.organizationId
    ? organizationName(organization, account.organizationId)
    : "";
  const personalWorkspace = isPersonalAccount(account);
  const linkedSubscriptionLabel = subscription
    ? subscriptionLabel(subscription, subscription?.id || subscription?.subscriptionId)
    : "";

  const moreMenuItems = isProductAdmin
    ? [
        {
          key: "charges",
          label: "Charges",
          icon: <DollarOutlined />,
          onClick: () => navigate(paths.workspaceCharges(workspaceId)),
        },
        {
          key: "paymentMethods",
          label: "Payment methods",
          icon: <WalletOutlined />,
          onClick: () => navigate(paths.workspacePaymentMethods(workspaceId)),
        },
        {
          key: "edit",
          label: "Edit workspace",
          icon: <EditOutlined />,
          onClick: openEditWorkspace,
        },
        {
          type: "divider",
        },
        {
          key: "delete",
          label: (
            <Popconfirm
              title="Delete workspace?"
              description="This action cannot be undone."
              okText="Delete"
              okButtonProps={{ danger: true, loading: deleting }}
              onConfirm={handleDeleteWorkspace}
            >
              <span style={{ color: "#ff4d4f", display: "block", width: "100%" }}>
                Delete workspace
              </span>
            </Popconfirm>
          ),
          icon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
          danger: true,
        },
      ]
    : [];

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-2 gap-2 flex-wrap">
        <WidgetSectionTitle
          icon={<AppstoreOutlined />}
          title="Workspace"
          subtitle="Membership details, product links, and workspace access"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Space wrap size={8} align="center">
          <Button
            icon={<ArrowLeftOutlined />}
            style={workspaceButtonStyles.back}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Button
            icon={<ReloadOutlined />}
            style={workspaceButtonStyles.refresh}
            onClick={loadDetails}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(paths.workspaceCreate())}
          >
            New Workspace
          </Button>

          {account ? (
            <>
              {isProductAdmin && !personalWorkspace ? (
                <Button
                  icon={<ShopOutlined />}
                  style={workspaceButtonStyles.organization}
                  onClick={() => navigate(paths.workspaceOrganization(workspaceId))}
                >
                  Organization
                </Button>
              ) : null}

              {isProductAdmin && !subscription ? (
                <Button
                  type="default"
                  icon={<CreditCardOutlined />}
                  onClick={() => navigate(paths.workspaceSubscriptions(workspaceId))}
                >
                  Subscriptions
                </Button>
              ) : null}

              {canInvite ? (
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={openInvite}
                >
                  Invite user
                </Button>
              ) : null}

              {isProductAdmin ? (
                <Dropdown menu={{ items: moreMenuItems }} trigger={["click"]}>
                  <Button icon={<MoreOutlined />}>More</Button>
                </Dropdown>
              ) : null}
            </>
          ) : null}
        </Space>
      </div>

      {loading && !account ? (
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : !account ? (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Workspace not found"
          />
        </Card>
      ) : (
        <Space direction="vertical" size={10} style={{ width: "100%" }}>
          <Card
            size="small"
            style={workspaceStyles.softCard}
            className="ws-detail__info-card"
          >
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <div className="ws-detail__header-row">
                <div
                  className="ws-detail__icon"
                  style={{
                    background: workspaceStyles.iconBubble.background,
                    color: workspaceStyles.iconBubble.color,
                  }}
                >
                  <AppstoreOutlined />
                </div>
                <div className="ws-detail__text-block">
                  <Title level={4} className="ws-detail__name">
                    {account.name || "Workspace"}
                  </Title>
                  <Space wrap size={[4, 4]} style={{ marginTop: 5 }}>
                    <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                      {readable(account.product, "Product")}
                    </Tag>
                    <Tag color="green" style={{ marginInlineEnd: 0 }}>
                      {readable(account.accountType, "Workspace type")}
                    </Tag>
                    <Tag color={isProductAdmin ? "green" : "gold"} style={{ marginInlineEnd: 0 }}>
                      {isProductAdmin ? "Admin access" : "Member access"}
                    </Tag>
                    <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
                      {account.users?.length || 0} members
                    </Tag>
                  </Space>
                  {account.description ? (
                    <Text type="secondary" className="ws-detail__description">
                      {account.description}
                    </Text>
                  ) : null}
                </div>
              </div>
              <div className="ws-detail__descriptions">
                <Descriptions
                  bordered
                  size="small"
                  column={{ xs: 1, md: 3 }}
                  items={[
                    {
                      key: "subscription",
                      label: "Subscription",
                      children: subscription ? (
                        <Space wrap size={6}>
                          <Text strong>{linkedSubscriptionLabel}</Text>
                          {isProductAdmin ? (
                            <Tooltip title="View subscription details">
                              <Button
                                size="small"
                                shape="circle"
                                icon={<EyeOutlined />}
                                onClick={() => navigate(paths.workspaceSubscriptions(workspaceId))}
                              />
                            </Tooltip>
                          ) : null}
                        </Space>
                      ) : (
                        <Space wrap size={6}>
                          <Text type="secondary">No subscription selected</Text>
                          {isProductAdmin ? (
                            <Tooltip title="Manage subscriptions">
                              <Button
                                size="small"
                                shape="circle"
                                type="primary"
                                icon={<CreditCardOutlined />}
                                onClick={() => navigate(paths.workspaceSubscriptions(workspaceId))}
                              />
                            </Tooltip>
                          ) : null}
                        </Space>
                      ),
                    },
                    ...(!personalWorkspace
                      ? [
                          {
                            key: "business",
                            label: "Organization",
                            children: account.organizationId ? (
                              <Space wrap>
                                <Text strong>{linkedOrganizationName}</Text>
                                {isProductAdmin ? (
                                  <Button
                                    size="small"
                                    icon={<ShopOutlined />}
                                    onClick={() => navigate(paths.workspaceOrganization(workspaceId))}
                                  >
                                    Open
                                  </Button>
                                ) : null}
                              </Space>
                            ) : (
                              isProductAdmin ? (
                                <Button
                                  size="small"
                                  icon={<ShopOutlined />}
                                  onClick={() => navigate(paths.workspaceOrganization(workspaceId))}
                                >
                                  Add or select
                                </Button>
                              ) : (
                                <Text type="secondary">Not linked</Text>
                              )
                            ),
                          },
                        ]
                      : []),
                    {
                      key: "created",
                      label: "Created",
                      children: formatDateTime(account.createdAt) || "-",
                    },
                    {
                      key: "updated",
                      label: "Updated",
                      children: formatDateTime(account.updatedAt) || "-",
                    },
                    {
                      key: "id",
                      label: "Workspace ID",
                      children: account.id ? (
                        <CopyableText value={account.id} />
                      ) : (
                        "-"
                      ),
                    },
                  ]}
                />
              </div>
            </Space>
          </Card>

          <Card
            size="small"
            title={sectionTitle("My Membership")}
            style={{ borderRadius: 8, ...workspaceStyles.highlightedCard }}
            styles={{ body: { padding: 12 } }}
          >
            {myMembership ? (
              <WorkspaceMemberCard
                membership={myMembership}
                title="You"
                compact
                highlight
                onView={isProductAdmin ? () => viewMember(myMembership) : undefined}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Your membership details are not available for this workspace."
              />
            )}
          </Card>

          {isProductAdmin ? (
            <Card
              size="small"
              title={sectionTitle("Workspace Members")}
              extra={<Tag color="blue">{activeMembers.length} active</Tag>}
              style={{ borderRadius: 8 }}
              styles={{ body: { padding: 12 } }}
            >
              <WorkspaceMembers
                members={activeMembers.map((member) => ({
                  ...member,
                  displayTitle:
                    membershipUserId(member) === actorId
                      ? "You"
                      : membershipUserLabel(member),
                }))}
                emptyText="No active members found."
                canRemoveMember={canRemoveMember}
                onRemoveMember={removeMember}
                onViewMember={viewMember}
                removingMemberId={removingMemberId}
              />
            </Card>
          ) : (
            <Alert
              type="info"
              showIcon
              icon={<LockOutlined />}
              message="Product members are shown to product admins only."
            />
          )}

          {canInvite && pendingInvitations.length > 0 ? (
            <Card
              size="small"
              title={sectionTitle("Pending Invitations")}
              extra={<Tag color="gold">{pendingInvitations.length} pending</Tag>}
              style={{ borderRadius: 8 }}
              styles={{ body: { padding: 12 } }}
            >
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                {pendingInvitations.map((membership) => (
                  <Card
                    key={membership.id || membershipUserId(membership)}
                    size="small"
                    style={{ borderRadius: 8 }}
                    styles={{ body: { padding: 10 } }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={{ minWidth: 0, width: "100%" }}>
                        <Text
                          strong
                          style={{
                            display: "block",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {membershipUserLabel(membership)}
                        </Text>
                        {membershipUserEmail(membership) ? (
                          <Text
                            type="secondary"
                            style={{ display: "block", fontSize: 12, wordBreak: "break-word" }}
                          >
                            {membershipUserEmail(membership)}
                          </Text>
                        ) : null}
                        <Space wrap size={[4, 4]} style={{ marginTop: 6 }}>
                          {(membership.roleNames || []).map((role) => (
                            <Tag key={role} color="blue" style={{ marginInlineEnd: 0 }}>
                              {readable(role)}
                            </Tag>
                          ))}
                          {(membership.privilegeScopes || []).map((scope) => (
                            <Tag key={scope} color="cyan" style={{ marginInlineEnd: 0 }}>
                              {readable(scope)}
                            </Tag>
                          ))}
                        </Space>
                        <Descriptions
                          size="small"
                          column={{ xs: 1, md: 1, xl: 3 }}
                          style={{ marginTop: 6, width: "100%" }}
                          items={[
                            {
                              key: "sent",
                              label: "Sent",
                              children: formatDateTime(membership.createdAt) || "-",
                            },
                            {
                              key: "updated",
                              label: "Updated",
                              children: formatDateTime(membership.updatedAt) || "-",
                            },
                            {
                              key: "invitedBy",
                              label: "Sent by",
                              children: membership.invitedBy ? (
                                <CopyableText value={membership.invitedBy} />
                              ) : (
                                "-"
                              ),
                            },
                            {
                              key: "link",
                              label: "Invite link",
                              children: membership.id ? (
                                <CopyableText
                                  value={workspaceInvitationLink(workspaceId, membership.id)}
                                  displayValue="Copy link"
                                />
                              ) : (
                                "-"
                              ),
                            },
                          ]}
                        />
                      </div>
                      <div>
                        <Popconfirm
                          title="Withdraw invitation?"
                          description="This pending invitation will no longer be available."
                          okText="Withdraw"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => withdrawInvitation(membership)}
                        >
                          <Button danger>Withdraw</Button>
                        </Popconfirm>
                      </div>
                    </div>
                  </Card>
                ))}
              </Space>
            </Card>
          ) : null}
        </Space>
      )}

      <Modal
        title={sectionTitle("Edit Workspace")}
        open={editOpen}
        onCancel={closeEditWorkspace}
        onOk={() => editForm.submit()}
        okText="Save"
        confirmLoading={updatingWorkspace}
        destroyOnHidden
      >
        <Form
          form={editForm}
          layout="vertical"
          requiredMark={false}
          onFinish={submitWorkspaceUpdate}
        >
          <Form.Item
            label="Workspace name"
            name="name"
            rules={[{ required: true, message: "Enter workspace name." }]}
          >
            <Input placeholder="Workspace name" autoFocus />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} placeholder="Optional workspace description" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={sectionTitle("Invite User")}
        open={inviteOpen}
        onCancel={closeInvite}
        onOk={() => inviteForm.submit()}
        okText="Send invitation"
        confirmLoading={inviting}
        destroyOnHidden
      >
        <Form
          form={inviteForm}
          layout="vertical"
          requiredMark={false}
          onFinish={submitInvite}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Invites are available for organization workspaces."
          />
          <Form.Item
            label="Email"
            name="email"
            rules={[{ type: "email", message: "Enter a valid email address" }]}
            extra="Use the email for the user you want to invite."
          >
            <Input placeholder="member@example.com" autoFocus />
          </Form.Item>

          <Form.Item
            label="User ID"
            name="userId"
            extra="Optional. If provided with email, it must match the same user."
          >
            <Input placeholder="00000000-0000-0000-0000-000000000000" />
          </Form.Item>

          <Form.Item
            label="Roles"
            name="roleIds"
            extra="Optional. Pick one or more roles to grant after acceptance."
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              loading={lookupLoading}
              options={visibleRoleOptions}
              placeholder="Search roles"
              onSearch={setRoleSearch}
              onClear={() => setRoleSearch("")}
              notFoundContent={
                roleSearch.trim()
                  ? lookupLoading
                    ? "Loading roles..."
                    : "No roles found"
                  : "Start typing to search roles"
              }
              optionFilterProp="searchText"
              filterOption={(input, option) =>
                option?.searchText?.includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label="Privileges"
            name="privilegeIds"
            extra="Optional. Pick specific privileges to grant after acceptance."
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              loading={lookupLoading}
              options={visiblePrivilegeOptions}
              placeholder="Search privileges"
              onSearch={setPrivilegeSearch}
              onClear={() => setPrivilegeSearch("")}
              notFoundContent={
                privilegeSearch.trim()
                  ? lookupLoading
                    ? "Loading privileges..."
                    : "No privileges found"
                  : "Start typing to search privileges"
              }
              optionFilterProp="searchText"
              filterOption={(input, option) =>
                option?.searchText?.includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkspaceDetailsPage;
